import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Pill, PackageX, Star, ShieldCheck, Truck, BadgeCheck, ChevronLeft, Loader2, Send } from "lucide-react";
import AddToCartBtn, { unitTextOf } from "../../storefront/AddToCartBtn";
import { SHOP } from "../../storefront/StoreLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value = 5, size = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function StoreMedicine() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [rev, setRev] = useState({ reviews: [], avg: 0, count: 0 });
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = () => {
    axios.get(`${API}/public/medicine/${id}`).then(({ data }) => setData(data)).catch(() => setError(true));
    axios.get(`${API}/public/reviews`, { params: { medicine_id: id } }).then(({ data }) => setRev(data)).catch(() => {});
  };
  useEffect(() => { setData(null); setRev({ reviews: [], avg: 0, count: 0 }); setError(false); setSubmitted(false); load(); window.scrollTo(0, 0); }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/reviews`, { ...form, medicine_id: id });
      setSubmitted(true);
      setForm({ name: "", rating: 5, message: "" });
    } catch {
      alert("Review submit nahi ho paya — thodi der baad try karein.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="medicine-not-found">
        <PackageX className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Medicine nahi mili</h1>
        <p className="text-sm text-slate-500 mt-2">Ho sakta hai ye product ab available na ho.</p>
        <Link to="/store/shop" className="mt-5 inline-flex items-center gap-2 bg-[#064E3B] text-white font-semibold px-5 py-3 rounded-full text-sm">Shop par wapas jayein</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="flex justify-center py-24" data-testid="medicine-loading"><Loader2 className="w-6 h-6 animate-spin text-emerald-700" /></div>;
  }

  const m = data.medicine;
  const hasOffer = m.offer_price > 0;
  const discount = hasOffer && m.mrp > 0 ? Math.round((1 - m.offer_price / m.mrp) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="medicine-detail-page">
      <Link to="/store/shop" data-testid="medicine-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline mb-4">
        <ChevronLeft className="w-4 h-4" /> Shop par wapas
      </Link>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          {m.image ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${m.image}`} alt={m.name} data-testid="medicine-image"
              className="w-full aspect-square rounded-2xl object-cover border border-slate-100" />
          ) : (
            <div className={`w-full aspect-square rounded-2xl flex items-center justify-center ${m.in_stock ? "bg-emerald-50" : "bg-slate-100"}`}>
              {m.in_stock ? <Pill className="w-20 h-20 text-emerald-600" /> : <PackageX className="w-20 h-20 text-slate-300" />}
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700" data-testid="medicine-category">{m.category}{m.subcategory ? ` › ${m.subcategory}` : ""}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" data-testid="medicine-name">{m.name}</h1>
          {m.manufacturer && <div className="text-sm text-slate-500 mt-1">by <span className="font-semibold text-slate-700">{m.manufacturer}</span></div>}

          {rev.count > 0 && (
            <div className="flex items-center gap-2 mt-2" data-testid="medicine-rating-summary">
              <Stars value={Math.round(rev.avg)} />
              <span className="text-sm font-bold text-slate-800">{rev.avg}</span>
              <span className="text-xs text-slate-400">({rev.count} reviews)</span>
            </div>
          )}

          <div className="flex items-baseline gap-3 mt-4" data-testid="medicine-price-block">
            <span className="font-mono-num text-3xl font-extrabold text-emerald-900" data-testid="medicine-price">₹{(hasOffer ? m.offer_price : m.mrp).toFixed(2)}</span>
            {hasOffer && (
              <>
                <span className="font-mono-num text-base text-slate-400 line-through" data-testid="medicine-mrp">₹{m.mrp.toFixed(2)}</span>
                <span className="text-xs font-bold text-white bg-emerald-600 px-2 py-1 rounded-full" data-testid="medicine-discount">{discount}% OFF</span>
              </>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1">{unitTextOf(m)} • MRP (inclusive of all taxes)</div>

          <div className="mt-3" data-testid="medicine-stock-status">
            {m.in_stock
              ? <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">IN STOCK — same-day delivery Kashipur</span>
              : <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">OUT OF STOCK — WhatsApp par poochhein</span>}
          </div>

          <div className="mt-5 max-w-xs"><AddToCartBtn m={m} testid="medicine-add-to-cart-btn" /></div>

          <div className="mt-5 bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 text-sm" data-testid="medicine-info-table">
            {m.generic_name && (
              <div className="flex justify-between gap-4 px-4 py-3"><span className="text-slate-500 shrink-0">Salt / Generic</span><span className="font-semibold text-slate-800 text-right" data-testid="medicine-salt">{m.generic_name}</span></div>
            )}
            {m.manufacturer && (
              <div className="flex justify-between gap-4 px-4 py-3"><span className="text-slate-500 shrink-0">Manufacturer</span><span className="font-semibold text-slate-800 text-right" data-testid="medicine-manufacturer">{m.manufacturer}</span></div>
            )}
            <div className="flex justify-between gap-4 px-4 py-3"><span className="text-slate-500 shrink-0">Pack Size</span><span className="font-semibold text-slate-800 text-right" data-testid="medicine-pack">{unitTextOf(m)}</span></div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-center" data-testid="medicine-trust-badges">
            {[{ icon: BadgeCheck, t: "100% Genuine" }, { icon: Truck, t: "Same-day Delivery" }, { icon: ShieldCheck, t: "Licensed Pharmacy" }].map((b) => (
              <div key={b.t} className="bg-emerald-50/70 border border-emerald-100 rounded-xl px-2 py-3">
                <b.icon className="w-5 h-5 text-emerald-700 mx-auto mb-1" />
                <div className="text-[10px] font-bold text-emerald-900">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 bg-white border border-slate-200 rounded-3xl p-6" data-testid="medicine-description-section">
        <h2 className="font-display text-lg font-bold text-slate-900">About {m.name}</h2>
        {m.description ? (
          <p className="text-sm text-slate-600 leading-relaxed mt-2 whitespace-pre-line" data-testid="medicine-description">{m.description}</p>
        ) : (
          <p className="text-sm text-slate-400 leading-relaxed mt-2">Is medicine ki detailed jaankari jald add ki jayegi. Abhi ke liye pharmacist se {SHOP.phoneDisplay} par baat kar sakte hain (8 AM – 10 PM).</p>
        )}
        <p className="text-[11px] text-slate-400 mt-3 border-t border-slate-100 pt-3">Note: Dawa kaise leni hai aur kitni leni hai — ye sirf doctor ya pharmacist ki salah se lein. Website par di gayi jaankari general information ke liye hai.</p>
      </div>

      <div className="mt-8 grid lg:grid-cols-5 gap-6 items-start" data-testid="medicine-reviews-section">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-6">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
            Customer Reviews
            {rev.count > 0 && <span className="text-xs font-semibold text-slate-400">({rev.count})</span>}
          </h2>
          <div className="mt-4 space-y-4">
            {rev.reviews.length === 0 && <p className="text-sm text-slate-400" data-testid="reviews-empty">Abhi koi review nahi — pehle review aap likhein!</p>}
            {rev.reviews.map((r) => (
              <div key={r.id} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-extrabold">{(r.name || "?")[0].toUpperCase()}</span>
                    <span className="text-sm font-bold text-slate-800">{r.name}</span>
                  </div>
                  <Stars value={r.rating} size="w-3.5 h-3.5" />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{r.message}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-6">
          <h3 className="font-display text-base font-bold text-slate-900">Apna Review Likhein</h3>
          {submitted ? (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800" data-testid="review-submitted">
              Shukriya! Aapka review humein mil gaya hai — approval ke baad yahan dikhega.
            </div>
          ) : (
            <form onSubmit={submitReview} className="mt-4 space-y-3" data-testid="review-form">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aapka naam *" data-testid="review-name-input" maxLength={60}
                className="w-full h-10 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              <div className="flex items-center gap-2" data-testid="review-rating-input">
                <span className="text-xs text-slate-500">Rating:</span>
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-star-${i}`} aria-label={`${i} star`}>
                    <Star className={`w-6 h-6 ${i <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
              <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Is medicine ke baare mein apna experience likhein *" data-testid="review-message-input" rows={3} maxLength={500}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              <button type="submit" disabled={submitting} data-testid="review-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] disabled:opacity-50 text-white font-semibold py-3 rounded-full text-sm transition-colors">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Review Submit Karein
              </button>
              <p className="text-[11px] text-slate-400 text-center">Review pharmacy team approve karne ke baad website par dikhta hai.</p>
            </form>
          )}
        </div>
      </div>

      {data.related?.length > 0 && (
        <div className="mt-10" data-testid="related-products-section">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Related Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.related.map((r) => (
              <Link key={r.id} to={`/store/medicine/${r.id}`} data-testid={`related-product-${r.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col hover:shadow-lg hover:border-emerald-200 transition-all">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{r.category}</div>
                <div className="font-semibold text-slate-900 text-sm leading-snug mt-0.5 flex-1">{r.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.generic_name || r.manufacturer || "—"}</div>
                <div className="flex items-center justify-between mt-3">
                  {r.offer_price > 0 ? (
                    <span className="flex items-baseline gap-1.5">
                      <span className="font-mono-num font-bold text-emerald-900">₹{r.offer_price.toFixed(2)}</span>
                      <span className="font-mono-num text-[11px] text-slate-400 line-through">₹{r.mrp.toFixed(2)}</span>
                    </span>
                  ) : (
                    <span className="font-mono-num font-bold text-slate-900">₹{r.mrp.toFixed(2)}</span>
                  )}
                  {r.in_stock && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">IN STOCK</span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
